<script setup lang="ts">
import { QDialog } from 'quasar'
import { computed } from 'vue'
import { Color } from '@previo/codebooks'
import PCard from '../card/PCard.vue'
import { PCardRound, PCardSize } from '../card/types'
import PBtn from '../btn/PBtn.vue'
import { PSpaceSize } from '../space/types'
import { PBtnType } from '../btn/types'
import { PMaxLines } from '../typography/types'
import PSpace from '../space/PSpace.vue'
import PTextTitle1 from '../typography/title/PTextTitle1.vue'
import { usePrevioDS } from '../../composables/helpers'
import { PDialogPosition } from './types'

const model = defineModel<boolean>()

interface Props {
  title?: string

  primaryBtn?: null | string
  primaryBtnColor?: null | Color.POSITIVE | Color.NEGATIVE_STATE
  primaryBtnDisable?: boolean
  primaryBtnLoading?: boolean

  /** @deprecated Secondary button in PModalDialog is deprecated */
  secondaryBtn?: null | string
  secondaryBtnDisable?: boolean
  secondaryBtnLoading?: boolean

  tertiaryBtn?: null | string
  tertiaryBtnDisable?: boolean
  tertiaryBtnLoading?: boolean

  persistent?: boolean
  noRefocus?: boolean
  noFocus?: boolean

  position?: null | PDialogPosition.STANDARD | PDialogPosition.TOP

  cy: string
}

type Emits = {
  (e: 'primary', evt: Event): void
  (e: 'secondary', evt: Event): void
  (e: 'tertiary', evt: Event): void
  (e: 'close'): void
}

const props = withDefaults(defineProps<Props>(), {
  primaryBtnColor: Color.POSITIVE,
  primaryBtnDisable: false,
  primaryBtnLoading: false,
  secondaryBtnDisable: false,
  secondaryBtnLoading: false,
  tertiaryBtnDisable: false,
  tertiaryBtnLoading: false,
  persistent: false,
  noRefocus: false,
  noFocus: false,
  position: PDialogPosition.STANDARD,
})
const emits = defineEmits<Emits>()

const ds = usePrevioDS()

const dialogPosition = computed<PDialogPosition.STANDARD | PDialogPosition.TOP | null>(() => {
  return props.position
})

const hasButton = computed<boolean>(() => {
  return !!props.primaryBtn || !!props.secondaryBtn || !!props.tertiaryBtn
})

const hasButtonGrow = computed<boolean>(() => {
  return ds.screen().isMobile
})

const onClose = () => {
  model.value = false
  emits('close')
}
</script>

<template>
  <q-dialog
    v-model="model"
    :position="dialogPosition ?? undefined"
    :transition-duration="200"
    :persistent="persistent"
    :no-refocus="noRefocus"
    :no-focus="noFocus"
    full-width
    @hide="onClose"
  >
    <p-card
      class="ds-modal-dialog-card p-display-flex p-flex-direction-column"
      :pa-mobile="PCardSize.SM"
      :pa-laptop="PCardSize.MD"
      :color="Color.WHITE"
      :round="PCardRound.LG"
      :data-cy="cy"
    >
      <div class="p-display-flex p-items-center p-no-wrap">
        <p-text-title1 :text="title" :color="Color.GREY_600" :max-lines="PMaxLines.ONE" />
        <p-space :mobile="PSpaceSize.GROW" />
        <p-space :mobile="PSpaceSize.LG" />
        <p-btn :type="PBtnType.OUTLINE" icon-end="xmark-large" cy="close" @click="onClose" />
      </div>

      <p-space :mobile="PSpaceSize.SM" vertical />

      <div class="ds-modal-dialog-content p-overflow-auto">
        <slot />
      </div>

      <div v-if="hasButton" data-menu-boundary>
        <p-space :mobile="PSpaceSize.SM" vertical />
        <div class="p-display-flex p-no-wrap">
          <p-space :mobile="PSpaceSize.GROW" />

          <template v-if="tertiaryBtn">
            <p-btn
              :text="tertiaryBtn"
              :color="Color.PRIMARY"
              :disable="tertiaryBtnDisable"
              :loading="tertiaryBtnLoading"
              :type="PBtnType.OUTLINE"
              :grow="hasButtonGrow"
              cy="tertiary"
              @click="(e) => emits('tertiary', e)"
            />
            <p-space :mobile="PSpaceSize.SM" />
          </template>

          <template v-if="secondaryBtn">
            <p-btn
              :text="secondaryBtn"
              :color="Color.PRIMARY"
              :disable="secondaryBtnDisable"
              :loading="secondaryBtnLoading"
              :type="PBtnType.SECONDARY"
              :grow="hasButtonGrow"
              cy="secondary"
              @click="(e) => emits('secondary', e)"
            />
            <p-space :mobile="PSpaceSize.SM" />
          </template>

          <p-btn
            v-if="primaryBtn"
            :text="primaryBtn"
            :color="primaryBtnColor"
            :disable="primaryBtnDisable"
            :loading="primaryBtnLoading"
            :grow="hasButtonGrow"
            cy="primary"
            @click="(e) => emits('primary', e)"
          />
        </div>
      </div>
    </p-card>
  </q-dialog>
</template>

<style scoped lang="scss">
@import '../../assets/scss/shared/mixins';

.ds-modal-dialog-card {
  // Need for override Quasar max dialog width
  width: 640px !important;
}

@include mobile {
  .q-dialog__inner--top .ds-modal-dialog-card {
    @include safe-area-padding(top, $space-sm-mobile);
  }
}

.ds-modal-dialog-content {
  flex: 1 1 auto;
}
</style>
